import type { Vehicle, Expense, VehicleEvent } from './types';

const BASE = '/api/vehicles';

// Auth-endpoints (login, 2FA-trin, me, profil m.m.) håndterer selv deres 401'ere.
// De må IKKE trigge auto-logout/redirect — ellers ryger 2FA- og password-flows i
// en redirect-loop i stedet for at vise "forkert kode" o.l.
function isAuthEndpoint(url: string): boolean {
  return url.includes('/auth/');
}

// Central fetch wrapper — tilføjer Authorization header hvis token findes.
// Ved 401 uden for auth-flowet: token er udløbet/ugyldigt → ryd session og send til login.
export async function apiFetch(url: string, options: RequestInit = {}): Promise<Response> {
  const token = localStorage.getItem('rfo_token');
  const headers: Record<string, string> = {
    ...(options.headers as Record<string, string> ?? {}),
  };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  if (options.body && typeof options.body === 'string' && !headers['Content-Type']) {
    headers['Content-Type'] = 'application/json';
  }
  const res = await fetch(url, { ...options, headers });

  if (res.status === 401 && !isAuthEndpoint(url)) {
    localStorage.removeItem('rfo_token');
    localStorage.removeItem('rfo_user');
    if (window.location.pathname !== '/login') {
      window.location.href = '/login';
    }
  }
  return res;
}

// Central JSON-helper: kaster ved !res.ok, så fejl-JSON ikke stille parses som data.
async function apiJson<T>(url: string, options: RequestInit = {}): Promise<T> {
  const res = await apiFetch(url, options);
  if (!res.ok) {
    let msg = `Fejl (${res.status})`;
    try { const b = await res.json(); if (b?.error) msg = b.error; } catch { /* ignorér parse-fejl */ }
    throw new Error(msg);
  }
  return res.json();
}

// Variant til endpoints uden svar-body (returnerer void).
async function apiVoid(url: string, options: RequestInit = {}): Promise<void> {
  const res = await apiFetch(url, options);
  if (!res.ok) {
    let msg = `Fejl (${res.status})`;
    try { const b = await res.json(); if (b?.error) msg = b.error; } catch { /* ignorér parse-fejl */ }
    throw new Error(msg);
  }
}

export interface VehiclesResponse {
  data: Vehicle[];
  total: number;
}

export async function getVehicles(params: Record<string, string> = {}): Promise<VehiclesResponse> {
  const qs = new URLSearchParams(params).toString();
  return apiJson<VehiclesResponse>(`${BASE}${qs ? '?' + qs : ''}`);
}

// Enkelt koeretoej via /:id — returnerer bilen uanset status (modsat liste-endpointet
// der som default skjuler solgte). Bruges af VehiclePage saa solgte biler kan aabnes.
export async function getVehicle(id: number): Promise<Vehicle> {
  return apiJson<Vehicle>(`${BASE}/${id}`);
}

export async function searchVehicles(q: string, status?: string): Promise<{ id: number; registration_number: string; make?: string; model?: string }[]> {
  if (!q) return [];
  const qs = new URLSearchParams({ q });
  if (status) qs.set('status', status);
  return apiJson<{ id: number; registration_number: string; make?: string; model?: string }[]>(`${BASE}/search?${qs}`);
}

export async function createVehicle(data: Vehicle): Promise<{ id: number }> {
  return apiJson<{ id: number }>(BASE, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
}

export async function updateVehicle(id: number, data: Vehicle): Promise<void> {
  await apiVoid(`${BASE}/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
}

export async function deleteVehicle(id: number): Promise<void> {
  await apiVoid(`${BASE}/${id}`, { method: 'DELETE' });
}

export async function getEvents(vehicleId: number): Promise<VehicleEvent[]> {
  return apiJson<VehicleEvent[]>(`/api/events/${vehicleId}`);
}

export async function createEvent(type: 'inspection' | 'maintenance' | 'checkup', data: object): Promise<unknown> {
  return apiJson<unknown>(`/api/events/${type}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
}

export async function updateEvent(type: 'inspection' | 'maintenance' | 'checkup', id: number, data: object): Promise<unknown> {
  return apiJson<unknown>(`/api/events/${type}/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
}

export async function deleteEvent(type: string, id: number) {
  await apiVoid(`/api/events/${type}/${id}`, { method: 'DELETE' });
}

export interface VehicleSpecs {
  vehicle_id?: number;
  front_axle?: string;
  rear_axle?: string;
  bolt_pattern?: string;
  center_bore?: string;
  tyre_pressure_front?: number;
  tyre_pressure_rear?: number;
  tyre_pressure_front_loaded?: number;
  tyre_pressure_rear_loaded?: number;
  tyre_pressure_standard?: number;
  oil_type?: string;
  oil_capacity?: number;
  wiper_front?: string;
  wiper_front_passenger?: string;
  wiper_rear?: string;
  notes?: string;
}

export async function getVehicleSpecs(vehicleId: number): Promise<VehicleSpecs | null> {
  return apiJson<VehicleSpecs | null>(`${BASE}/${vehicleId}/specs`);
}

export interface OverviewPeriod {
  period_start:      string;
  period_end:        string;
  days_total:        number;
  days_offline:      number;
  days_online:       number;
  weeks_online:      number;
  rental_count:      number;
  total_km:          number;
  earnings:          number;
  earnings_excl_vat: number;
  expenses:          number;
  expenses_excl_vat: number;
  net:               number;
  net_excl_vat:      number;
  time_hours:        number;
  per_rental:        number | null;
  km_per_rental:     number | null;
  per_km:            number | null;
  per_day:           number | null;
  per_week:          number | null;
  per_month:         number | null;
}

export interface VehicleOverview {
  listed_on:    string | null;
  ytd:          OverviewPeriod;
  since_listed: OverviewPeriod | null;
}

export async function getVehicleOverview(vehicleId: number): Promise<VehicleOverview> {
  return apiJson<VehicleOverview>(`${BASE}/${vehicleId}/overview`);
}

export async function saveVehicleSpecs(vehicleId: number, data: VehicleSpecs): Promise<void> {
  await apiVoid(`${BASE}/${vehicleId}/specs`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
}

export interface FleetEvent {
  id: number;
  event_type: 'maintenance' | 'checkup';
  date: string;
  description: string | null;
  workshop: string | null;
  registration_number: string;
}

export async function getFleetEvents(params: { from?: string; to?: string } = {}): Promise<FleetEvent[]> {
  const qs = new URLSearchParams();
  if (params.from) qs.set('from', params.from);
  if (params.to)   qs.set('to', params.to);
  return apiJson<FleetEvent[]>(`/api/events/fleet${qs.toString() ? '?' + qs : ''}`);
}

export async function getExpenses(params: { vehicle_id?: number; fleet?: boolean } = {}): Promise<Expense[]> {
  const qs = new URLSearchParams();
  if (params.vehicle_id != null) qs.set('vehicle_id', String(params.vehicle_id));
  if (params.fleet) qs.set('fleet', 'true');
  return apiJson<Expense[]>(`/api/expenses${qs.toString() ? '?' + qs : ''}`);
}

export async function createExpense(data: Omit<Expense, 'id' | 'registration_number'>): Promise<{ id: number }> {
  return apiJson<{ id: number }>('/api/expenses', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
}

export async function updateExpense(id: number, data: Omit<Expense, 'id' | 'registration_number'>): Promise<void> {
  await apiVoid(`/api/expenses/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteExpense(id: number): Promise<void> {
  await apiVoid(`/api/expenses/${id}`, { method: 'DELETE' });
}

// ── Expense attachments ───────────────────────────────────────────────────────

export interface ExpenseAttachment {
  id: number;
  image_url: string;
  filename: string;
  mime_type: string | null;
  size_bytes: number | null;
  source: 'manual' | 'email' | 'ai_scan';
  sender_email: string | null;
  created_at: string;
}

export async function getExpenseAttachments(expenseId: number): Promise<ExpenseAttachment[]> {
  const res = await apiFetch(`/api/expenses/${expenseId}/attachments`);
  if (!res.ok) throw new Error('Kunne ikke hente bilag');
  return res.json();
}

export async function uploadExpenseAttachment(expenseId: number, file: File): Promise<ExpenseAttachment> {
  const form = new FormData();
  form.append('file', file);
  const res = await apiFetch(`/api/expenses/${expenseId}/attachments`, { method: 'POST', body: form });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error ?? 'Upload fejlede');
  return data;
}

export async function deleteExpenseAttachment(expenseId: number, attachmentId: number): Promise<void> {
  const res = await apiFetch(`/api/expenses/${expenseId}/attachments/${attachmentId}`, { method: 'DELETE' });
  if (!res.ok) { const e = await res.json(); throw new Error(e.error ?? 'Kunne ikke slette'); }
}

// ── Recurring expenses ────────────────────────────────────────────────────────

export interface RecurringExpense {
  id: number;
  customer_id?: number;
  vehicle_id?: number | null;
  registration_number?: string | null;
  category: string;
  description?: string | null;
  amount: number;
  vat_status: string;
  vat_amount?: number | null;
  notes?: string | null;
  frequency: 'monthly' | 'yearly';
  next_due_date: string;
  active?: boolean;
}

export async function getRecurringExpenses(): Promise<RecurringExpense[]> {
  return apiJson<RecurringExpense[]>('/api/recurring');
}

export async function getDueRecurringExpenses(): Promise<RecurringExpense[]> {
  return apiJson<RecurringExpense[]>('/api/recurring/due');
}

export async function createRecurringExpense(data: Omit<RecurringExpense, 'id'>): Promise<{ id: number }> {
  return apiJson<{ id: number }>('/api/recurring', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateRecurringExpense(id: number, data: Partial<RecurringExpense>): Promise<void> {
  await apiVoid(`/api/recurring/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function approveRecurringExpense(id: number, data: Partial<RecurringExpense>): Promise<{ expense_id: number; next_due_date: string }> {
  return apiJson<{ expense_id: number; next_due_date: string }>(`/api/recurring/${id}/approve`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function deleteRecurringExpense(id: number): Promise<void> {
  await apiVoid(`/api/recurring/${id}`, { method: 'DELETE' });
}

export interface ScanQuota {
  plan: string;
  haiku_used: number;
  haiku_limit: number | null;
  sonnet_used: number;
  sonnet_limit: number;
}

export interface ScanResult {
  fields: {
    date?: string | null;
    amount?: number | null;
    vat_amount?: number | null;
    description?: string | null;
    vendor?: string | null;
    category?: string | null;
    currency?: string | null;
  };
  quota: ScanQuota;
}

export async function scanReceipt(file: File, useSonnet = false): Promise<ScanResult> {
  const form = new FormData();
  form.append('file', file);
  form.append('use_sonnet', String(useSonnet));
  const res = await apiFetch('/api/ai/scan-receipt', { method: 'POST', body: form });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw Object.assign(new Error(err.error ?? 'scan_failed'), err);
  }
  return res.json();
}

export async function getAiQuota(): Promise<ScanQuota> {
  return apiJson<ScanQuota>('/api/ai/quota');
}

export interface Booking {
  id: number;
  uid: string;
  source: string;
  vehicle_id: number | null;
  registration_number: string;
  renter_id: number | null;
  renter_name: string | null;
  renter_phone: string | null;
  renter_email: string | null;
  starts_at: string;
  ends_at: string;
  actual_start_at: string | null;
  actual_end_at: string | null;
  rental_duration_hours: number | null;
  actual_duration_hours: number | null;
  km_start: number | null;
  km_end: number | null;
  km_included: number | null;
  km_extra: number | null;
  km_overage_rate: number | null;
  km_extra_rate: number | null;
  fuel_start_pct: number | null;
  fuel_end_pct: number | null;
  fuel_missing_rate: number | null;
  rental_amount: number | null;
  discount: number | null;
  excess_reduction: number | null;
  excess_risk_amount: number | null;
  extra_driver_fee: number | null;
  foreign_driving_fee: number | null;
  handling_fees: number | null;
  payout: number | null;
  invoice_number: string | null;
  invoice_date: string | null;
  fuel_adjustment: number | null;
  km_overage_amount: number | null;
  km_extra_amount: number | null;
  late_return_fee: number | null;
  dynamic_pricing: number | null;
  cancelled: boolean;
  cancelled_at: string | null;
  cancellation_fee: number | null;
  notes: string | null;
  synced_at: string;
  created_at: string;
}

export interface SyncResult {
  created: number;
  updated: number;
  cancelled: number;
  total: number;
  feedTimestamp?: string;
}

export async function getBookings(params: { from?: string; to?: string; vehicle_id?: number } = {}): Promise<Booking[]> {
  const qs = new URLSearchParams();
  if (params.from) qs.set('from', params.from);
  if (params.to)   qs.set('to', params.to);
  if (params.vehicle_id != null) qs.set('vehicle_id', String(params.vehicle_id));
  return apiJson<Booking[]>(`/api/bookings${qs.toString() ? '?' + qs : ''}`);
}

export async function clearSyncedBookings(): Promise<{ deleted: number }> {
  return apiJson<{ deleted: number }>('/api/bookings/gomore', { method: 'DELETE' });
}

// Perioder hvor en bil er blokeret for udlejning (værksted, syn, eftersyn). Tider i UTC.
export interface VehicleBlock {
  kind: 'maintenance' | 'inspection' | 'checkup' | 'offline';
  source_id: number;
  vehicle_id: number;
  registration_number: string;
  title: string;
  starts_at: string;
  ends_at: string;
}
export async function getVehicleBlocks(params: { from?: string; to?: string } = {}): Promise<VehicleBlock[]> {
  const qs = new URLSearchParams();
  if (params.from) qs.set('from', params.from);
  if (params.to) qs.set('to', params.to);
  return apiJson<VehicleBlock[]>(`/api/bookings/blocks${qs.toString() ? '?' + qs : ''}`);
}

// Private kalender-URL'er til Google Kalender: 'blocks' (blokerede biler) og 'bookings'
// (udlejninger), hver med sin nøgle. null → slået fra.
export type CalendarFeedScope = 'blocks' | 'bookings';
export type CalendarFeedTokens = Record<CalendarFeedScope, string | null>;
export async function getCalendarFeeds(): Promise<CalendarFeedTokens> {
  return apiJson<CalendarFeedTokens>('/api/settings/calendar-feed');
}
export async function createCalendarFeed(scope: CalendarFeedScope): Promise<CalendarFeedTokens> {
  return apiJson<CalendarFeedTokens>(`/api/settings/calendar-feed/${scope}`, { method: 'POST' });
}
export async function deleteCalendarFeed(scope: CalendarFeedScope): Promise<CalendarFeedTokens> {
  return apiJson<CalendarFeedTokens>(`/api/settings/calendar-feed/${scope}`, { method: 'DELETE' });
}
// Personligt link pr. medarbejder — kun de opgaver medarbejderen er sat på
export async function getEmployeeCalendarFeeds(): Promise<Record<number, string | null>> {
  return apiJson<Record<number, string | null>>('/api/users/calendar-feeds');
}
export async function createEmployeeCalendarFeed(userId: number): Promise<{ token: string | null }> {
  return apiJson<{ token: string | null }>(`/api/users/${userId}/calendar-feed`, { method: 'POST' });
}
export async function deleteEmployeeCalendarFeed(userId: number): Promise<{ token: string | null }> {
  return apiJson<{ token: string | null }>(`/api/users/${userId}/calendar-feed`, { method: 'DELETE' });
}
export const calendarFeedUrl = (token: string) => `${window.location.origin}/api/public/calendar/${token}.ics`;

// Sidste vellykkede kalendersync for kunden — knap eller timelig automatisk kørsel (ISO-tid)
export async function getSyncStatus(): Promise<{ last_synced: string | null }> {
  return apiJson<{ last_synced: string | null }>('/api/bookings/sync/status');
}

export async function syncCalendar(): Promise<SyncResult> {
  const res = await apiFetch('/api/bookings/sync/gomore', { method: 'POST' });
  if (!res.ok) {
    const err = await res.json();
    throw new Error(err.error ?? 'Sync fejlede');
  }
  return res.json();
}

export interface AppUser {
  id: number;
  name: string;
}

export async function getUsers(): Promise<AppUser[]> {
  return apiJson<AppUser[]>('/api/users');
}

export async function createUser(name: string): Promise<AppUser> {
  return apiJson<AppUser>('/api/users', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ name }),
  });
}

export async function updateUser(id: number, name: string): Promise<void> {
  await apiVoid(`/api/users/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ name }),
  });
}

export async function deleteUser(id: number): Promise<void> {
  await apiVoid(`/api/users/${id}`, { method: 'DELETE' });
}

export async function getSettings(): Promise<Record<string, unknown>> {
  return apiJson<Record<string, unknown>>('/api/settings');
}

export async function saveSettings(partial: Record<string, unknown>): Promise<void> {
  await apiVoid('/api/settings', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(partial),
  });
}

// ─── Vehicle gallery (Bolge 3 mellemtrin) ─────────────────────────────

export interface VehicleImage {
  id: number;
  image_url: string;
  sort_order: number;
}

export async function getVehicleImages(vehicleId: number): Promise<VehicleImage[]> {
  const res = await apiFetch(`${BASE}/${vehicleId}/images`);
  if (!res.ok) throw new Error('Kunne ikke hente galleri');
  return res.json();
}

export async function uploadVehicleImage(vehicleId: number, file: File): Promise<VehicleImage> {
  const form = new FormData();
  form.append('image', file);
  const res = await apiFetch(`${BASE}/${vehicleId}/images`, { method: 'POST', body: form });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error ?? 'Upload fejlede');
  return data;
}

export async function deleteVehicleImage(vehicleId: number, imageId: number): Promise<void> {
  const res = await apiFetch(`${BASE}/${vehicleId}/images/${imageId}`, { method: 'DELETE' });
  if (!res.ok) { const e = await res.json(); throw new Error(e.error ?? 'Kunne ikke slette'); }
}

export async function reorderVehicleImages(vehicleId: number, order: number[]): Promise<void> {
  const res = await apiFetch(`${BASE}/${vehicleId}/images/reorder`, {
    method: 'PUT',
    body: JSON.stringify({ order }),
  });
  if (!res.ok) { const e = await res.json(); throw new Error(e.error ?? 'Kunne ikke gemme raekkefoelge'); }
}

export async function uploadImage(file: File, registrationNumber?: string, vehicleId?: number): Promise<string> {
  const form = new FormData();
  form.append('image', file);
  if (registrationNumber) form.append('registration', registrationNumber);
  if (vehicleId != null) form.append('vehicle_id', String(vehicleId));
  const res = await apiFetch(`${BASE}/upload`, { method: 'POST', body: form });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error ?? 'Upload fejlede');
  return data.url;
}

// Importerede bookings (læst fra rfo_bookings)
export interface ImportedBooking {
  id: number;
  uid: string;
  source: string;
  vehicle_id: number | null;
  registration_number: string | null;
  invoice_number: string | null;
  invoice_date: string | null;
  rental_start: string | null;
  rental_end: string | null;
  renter_name: string | null;
  renter_phone: string | null;
  renter_email: string | null;
  rental_amount: number | null;
  discount: number | null;
  excess_reduction: number | null;
  excess_risk_amount: number | null;
  extra_driver_fee: number | null;
  foreign_driving_fee: number | null;
  handling_fees: number | null;
  vat_amount: number | null;
  payout: number | null;
  km_start: number | null;
  km_end: number | null;
  km_included: number | null;
  km_extra: number | null;
  fuel_start_pct: number | null;
  fuel_end_pct: number | null;
  actual_start_at: string | null;
  actual_end_at: string | null;
  rental_duration_hours: number | null;
  actual_duration_hours: number | null;
  fuel_adjustment: number | null;
  km_overage_amount: number | null;
  km_extra_amount: number | null;
  late_return_fee: number | null;
  dynamic_pricing: number | null;
  cancelled: boolean;
  cancelled_at: string | null;
  cancellation_fee: number | null;
  /** Sum af fakturalinjer i rfo_booking_extras (0 hvis ingen) */
  extras_total: number | null;
  /** Den momspligtige del af extras_total */
  extras_vat_total: number | null;
  /** Sum af betalingsposteringer matchet til bookingen. For manuelle fakturaer er det
   *  eneste sted betalingen findes — `payout` er NULL på dem. */
  received_total: number | null;
}

export interface BookingExtra {
  id: number;
  description: string;
  amount: number;
  vat_applicable: number | boolean;  // backend returnerer tinyint, vi accepterer begge
  sort_order: number;
}

export async function getBookingExtras(bookingId: number): Promise<BookingExtra[]> {
  const res = await apiFetch(`/api/rentals/invoices/${bookingId}/extras`);
  if (!res.ok) throw new Error('Kunne ikke hente ekstra-linjer');
  return res.json();
}

export async function saveBookingExtras(bookingId: number, extras: { description: string; amount: number; vat_applicable: boolean }[]): Promise<void> {
  const res = await apiFetch(`/api/rentals/invoices/${bookingId}/extras`, {
    method: 'PUT',
    body: JSON.stringify({ extras }),
  });
  if (!res.ok) { const e = await res.json(); throw new Error(e.error ?? 'Kunne ikke gemme ekstra-linjer'); }
}

// JSON import (platform-agnostisk)
export interface ImportPreviewItem {
  rental_id: string;
  status: 'new' | 'match' | 'update' | 'duplicate' | 'error';
  registration_number?: string | null;
  vehicle_id?: number | null;
  renter_name?: string | null;
  starts_at?: string | null;
  ends_at?: string | null;
  payout?: number | null;
  invoice_number?: string | null;
  error?: string;
  existing_id?: number;
  /** Felter hvor GoMore nu har andre tal end det gemte — sat når status er 'update' */
  changed?: string[];
}

export interface ImportResult {
  rental_id: string;
  status: 'imported' | 'merged' | 'updated' | 'duplicate' | 'error';
  id?: number;
  existing_id?: number;
  error?: string;
  /** Felter der blev opdateret med nyere værdier fra GoMore */
  changed?: string[];
}

export async function previewImportJson(rentals: unknown[]): Promise<{ items: ImportPreviewItem[] }> {
  const res = await apiFetch('/api/rentals/parse-json', {
    method: 'POST',
    body: JSON.stringify({ rentals }),
  });
  if (!res.ok) { const e = await res.json(); throw new Error(e.error ?? 'Parse fejlede'); }
  return res.json();
}

export async function importJson(
  rentals: unknown[],
  vehicle_overrides: Record<string, number | null> = {}
): Promise<{ results: ImportResult[] }> {
  const res = await apiFetch('/api/rentals/import-json', {
    method: 'POST',
    body: JSON.stringify({ rentals, vehicle_overrides }),
  });
  if (!res.ok) { const e = await res.json(); throw new Error(e.error ?? 'Import fejlede'); }
  return res.json();
}

export async function getImportedBookings(): Promise<ImportedBooking[]> {
  return apiJson<ImportedBooking[]>('/api/rentals/invoices');
}

export async function getBookingById(id: number): Promise<ImportedBooking> {
  const res = await apiFetch(`/api/bookings/${id}`);
  if (!res.ok) throw new Error('Booking findes ikke');
  return res.json();
}

export async function assignBookingVehicle(id: number, vehicle_id: number | null): Promise<void> {
  await apiVoid(`/api/rentals/invoices/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ vehicle_id }),
  });
}

// Markér en kalenderpost uden faktura som aflyst — til rettidige aflysninger, der ingen kvittering har
export async function setBookingCancelled(id: number, cancelled: boolean): Promise<void> {
  await apiVoid(`/api/bookings/${id}/cancelled`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ cancelled }),
  });
}

export async function updateImportedBooking(id: number, data: Partial<ImportedBooking>): Promise<void> {
  const res = await apiFetch(`/api/rentals/invoices/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
  if (!res.ok) { const e = await res.json(); throw new Error(e.error ?? 'Opdatering fejlede'); }
}

export async function deleteImportedBooking(id: number): Promise<void> {
  await apiVoid(`/api/rentals/invoices/${id}`, { method: 'DELETE' });
}

export async function createManualBooking(data: Record<string, unknown>): Promise<{ id: number }> {
  const res = await apiFetch('/api/rentals/invoices', {
    method: 'POST',
    body: JSON.stringify(data),
  });
  if (!res.ok) { const e = await res.json(); throw new Error(e.error ?? 'Oprettelse fejlede'); }
  return res.json();
}

// Auth
export interface AuthUser {
  id: number;
  name: string;
  email: string;
  role: string;
  isSuperadmin: boolean;
  customerId: number;
  emailVerified?: boolean;
  customerStatus?: 'active' | 'pending_review' | 'rejected' | 'suspended' | 'cancelled';
  pendingEmail?: string | null;  // hvis sat: bruger har anmodet email-skift, venter på verifikation
}

// Login response kan være ETEN fuld login (token + user) ELLER en 2FA-challenge.
// Frontend skal tjekke 'requires_2fa' før den bruger 'token'.
export type LoginResponse =
  | { token: string; user: AuthUser; requires_2fa?: false }
  | { requires_2fa: true; requires_totp: true; pre_auth_token: string; methods: TwoFactorMethod[] };

export async function login(email: string, password: string): Promise<LoginResponse> {
  const res = await fetch('/api/auth/login', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password }),
  });
  if (!res.ok) {
    const err = await res.json();
    throw new Error(err.error ?? 'Login fejlede');
  }
  return res.json();
}

export interface SignupPayload {
  email: string;
  password: string;
  name: string;
  company_name: string;
  city: string;
  plan: 'free' | 'plus' | 'pro';
}

export async function signup(data: SignupPayload): Promise<{ token: string; user: AuthUser; cityBlocked: boolean }> {
  const res = await fetch('/api/auth/signup', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!res.ok) {
    const err = await res.json();
    throw new Error(err.error ?? 'Oprettelse fejlede');
  }
  return res.json();
}

export async function verifyEmail(token: string): Promise<void> {
  const res = await fetch('/api/verification/verify', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token }),
  });
  if (!res.ok) {
    const err = await res.json();
    throw new Error(err.error ?? 'Verifikation fejlede');
  }
}

export async function resendVerification(): Promise<void> {
  const res = await apiFetch('/api/verification/resend', { method: 'POST' });
  if (!res.ok) throw new Error('Kunne ikke gensende verifikations-mail');
}

export async function getMe(): Promise<AuthUser> {
  const res = await apiFetch('/api/auth/me');
  if (!res.ok) throw new Error('Ikke logget ind');
  return res.json();
}

// Renters
export interface Renter {
  id: number;
  name: string;
  email: string | null;
  phone: string | null;
  city: string | null;
  drivers_license_number: string | null;
  created_at: string;
  rental_count: number;
}

export async function getRenters(): Promise<Renter[]> {
  const res = await apiFetch('/api/renters');
  if (!res.ok) throw new Error('Kunne ikke hente lejere');
  return res.json();
}

export interface RenterDetail {
  id: number;
  name: string;
  email: string | null;
  phone: string | null;
  address: string | null;
  postal_code: string | null;
  city: string | null;
  country: string | null;
  drivers_license_number: string | null;
  source: string | null;
  notes: string | null;
  created_at: string;
}

export interface RenterBookingSummary {
  id: number;
  uid: string;
  registration_number: string;
  source: string;
  s: string;
  e: string;
  invoice_number: string | null;
  invoice_date: string | null;
  payout: number;
  rental_amount: number;
  cancelled: number;
  cancellation_fee: number | null;
}

export async function getRenterDetail(id: number): Promise<{ renter: RenterDetail; bookings: RenterBookingSummary[] }> {
  const res = await apiFetch(`/api/renters/${id}`);
  if (!res.ok) throw new Error('Lejer findes ikke');
  return res.json();
}

export interface DashboardKpis {
  revenue_mtd: number;
  revenue_prev_period: number;  // prev maaned dag 1 til samme dag (klampet)
  revenue_prev_mtd: number;     // prev maaned hele
  rentals_mtd: number;
  rentals_total: number;
  rentals_30d: number;
  active_vehicles: number;
  total_vehicles: number;
  vehicles_in_service: number;
  utilization_total: number;
  utilization_30d: number;
  car_years: number;
  car_days_total: number;
}

export async function getDashboardKpis(): Promise<DashboardKpis> {
  const res = await apiFetch('/api/dashboard/kpis');
  if (!res.ok) throw new Error('Kunne ikke hente KPIs');
  return res.json();
}

export interface UpcomingServiceItem {
  vehicle_id: number;
  registration_number: string;
  kind: 'km' | 'date';
  km_remaining: number | null;    // km til service (i km; negativ = overskredet)
  days_remaining: number | null;  // dage til service (negativ = overskredet)
}

export async function getUpcomingService(): Promise<UpcomingServiceItem[]> {
  const res = await apiFetch('/api/dashboard/upcoming-service');
  if (!res.ok) throw new Error('Kunne ikke hente kommende service');
  return res.json();
}

export async function updateBooking(id: number, payload: {
  vehicle_id?: number | null;
  renter_id?: number | null;
  starts_at?: string;
  ends_at?: string;
}): Promise<void> {
  const res = await apiFetch(`/api/bookings/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.error || 'Kunne ikke opdatere booking');
  }
}

export async function getActiveRentals(limit = 10, sort: 'ends' | 'starts' = 'ends'): Promise<Booking[]> {
  const res = await apiFetch(`/api/dashboard/active-rentals?limit=${limit}&sort=${sort}`);
  if (!res.ok) throw new Error('Kunne ikke hente aktive udlejninger');
  return res.json();
}

export interface RenterSuggestion {
  id: number;
  name: string;
  email: string | null;
  phone: string | null;
}

export async function searchRenters(q: string): Promise<RenterSuggestion[]> {
  if (!q.trim()) return [];
  const res = await apiFetch(`/api/renters/search?q=${encodeURIComponent(q)}`);
  if (!res.ok) return [];
  return res.json();
}

// Hub: hent alle kunder (superadmin)
export interface Customer {
  id: number;
  company_name: string;
  city?: string;
  plan: string;
  status?: 'active' | 'pending_review' | 'rejected' | 'suspended' | 'cancelled';
  created_at: string;
}

export interface PendingCustomer {
  id: number;
  company_name: string;
  city: string;
  plan: string;
  created_at: string;
  owner_name: string;
  owner_email: string;
  owner_verified: number;
}

export async function getCustomers(): Promise<Customer[]> {
  return apiJson<Customer[]>('/api/hub/customers');
}

export async function getPendingCustomers(): Promise<PendingCustomer[]> {
  return apiJson<PendingCustomer[]>('/api/hub/customers/pending');
}

export async function approveCustomer(id: number, plan?: string): Promise<void> {
  await apiVoid(`/api/hub/customers/${id}/approve`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(plan ? { plan } : {}),
  });
}

export async function rejectCustomer(id: number): Promise<void> {
  await apiVoid(`/api/hub/customers/${id}/reject`, { method: 'POST' });
}

export async function createCustomer(data: { company_name: string; plan: string }): Promise<{ id: number }> {
  return apiJson<{ id: number }>('/api/hub/customers', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
}

export async function createLoginUser(data: { email: string; password: string; name: string; customer_id: number; role?: string }): Promise<{ id: number }> {
  const res = await apiFetch('/api/auth/create-user', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!res.ok) {
    const err = await res.json();
    throw new Error(err.error ?? 'Oprettelse fejlede');
  }
  return res.json();
}

// ─── Backup (Bølge 2 — Hub/superadmin) ─────────────────────────────────────

export type BackupType = 'data' | 'full';
export type BackupDestination = 'bunny' | 'download';
export type BackupStatus = 'queued' | 'running' | 'done' | 'failed';

export interface Backup {
  id: number;
  type: BackupType;
  destination: BackupDestination;
  filename: string;
  bunny_path: string | null;
  size_bytes: number | null;
  status: BackupStatus;
  error_msg: string | null;
  is_scheduled: boolean;
  created_at: string;
  started_at: string | null;
  completed_at: string | null;
}

export interface BackupSchedule {
  id: number;
  type: BackupType;
  destination: BackupDestination;
  frequency_minutes: number;
  hour: number;
  minute: number;
  day_of_week: number | null;
  day_of_month: number | null;
  enabled: boolean;
  last_run_at: string | null;
  next_run_at: string | null;
}

export async function getBackups(): Promise<Backup[]> {
  return apiJson<Backup[]>('/api/backup');
}

export async function getBackup(id: number): Promise<Backup> {
  return apiJson<Backup>(`/api/backup/${id}`);
}

export async function createBackup(data: { type: BackupType; destination: BackupDestination }): Promise<{ id: number }> {
  const res = await apiFetch('/api/backup', {
    method: 'POST',
    body: JSON.stringify(data),
  });
  if (!res.ok) {
    const err = await res.json();
    throw new Error(err.error ?? 'Backup-trigger fejlede');
  }
  return res.json();
}

export async function deleteBackup(id: number): Promise<void> {
  const res = await apiFetch(`/api/backup/${id}`, { method: 'DELETE' });
  if (!res.ok) {
    const err = await res.json();
    throw new Error(err.error ?? 'Kunne ikke slette');
  }
}

// Triger download i browseren — henter via auth'd fetch og gemmer som fil.
// Bemærk: hele filen buffes i memory før download. OK for data-backups (få MB),
// kan blive et problem for fulde backups i GB-størrelse.
// Hvis backenden kræver TOTP (2FA), kastes en BackupRequiresTotpError som caller
// skal håndtere ved at vise prompt og kalde igen med code.
export class BackupRequiresTotpError extends Error {
  methods: TwoFactorMethod[];
  constructor(methods: TwoFactorMethod[] = ['totp']) {
    super('2FA-kode er påkrævet');
    this.name = 'BackupRequiresTotpError';
    this.methods = methods;
  }
}

export async function downloadBackup(id: number, filename: string, totpCode?: string): Promise<void> {
  const res = await apiFetch(`/api/backup/${id}/download`, {
    method: 'POST',
    body: JSON.stringify(totpCode ? { code: totpCode } : {}),
  });
  if (res.status === 401) {
    const err = await res.json().catch(() => ({}));
    if (err.requires_totp || err.requires_2fa) throw new BackupRequiresTotpError(err.methods || ['totp']);
    throw new Error(err.error ?? 'Adgang nægtet');
  }
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error ?? `Download fejlede (${res.status})`);
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export async function getBackupSchedules(): Promise<BackupSchedule[]> {
  return apiJson<BackupSchedule[]>('/api/backup/schedule/list');
}

export async function createBackupSchedule(data: Partial<BackupSchedule>): Promise<{ id: number }> {
  const res = await apiFetch('/api/backup/schedule', {
    method: 'POST',
    body: JSON.stringify(data),
  });
  if (!res.ok) {
    const err = await res.json();
    throw new Error(err.error ?? 'Kunne ikke oprette schedule');
  }
  return res.json();
}

export async function updateBackupSchedule(id: number, data: Partial<BackupSchedule>): Promise<void> {
  const res = await apiFetch(`/api/backup/schedule/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
  if (!res.ok) {
    const err = await res.json();
    throw new Error(err.error ?? 'Kunne ikke opdatere schedule');
  }
}

export async function deleteBackupSchedule(id: number): Promise<void> {
  const res = await apiFetch(`/api/backup/schedule/${id}`, { method: 'DELETE' });
  if (!res.ok) {
    const err = await res.json();
    throw new Error(err.error ?? 'Kunne ikke slette schedule');
  }
}

// ─── 2FA (TOTP) ─────────────────────────────────────────────────────────────

export interface TotpStatus {
  enabled: boolean;
  enabled_at: string | null;
  recovery_codes_remaining: number;
  email_2fa_enabled: boolean;
  email_2fa_enabled_at: string | null;
}

export type TwoFactorMethod = 'totp' | 'email';

export interface TotpSetupResponse {
  secret: string;          // base32 — vises hvis QR ikke virker
  qr_data_url: string;     // PNG data URL
  recovery_codes: string[]; // plaintekst — kun nu
}

export async function getTotpStatus(): Promise<TotpStatus> {
  return apiJson<TotpStatus>('/api/auth/totp/status');
}

export async function setupTotp(): Promise<TotpSetupResponse> {
  const res = await apiFetch('/api/auth/totp/setup', { method: 'POST' });
  if (!res.ok) {
    const err = await res.json();
    throw new Error(err.error ?? 'Kunne ikke starte 2FA-opsætning');
  }
  return res.json();
}

export async function verifyTotpSetup(code: string): Promise<void> {
  const res = await apiFetch('/api/auth/totp/verify-setup', {
    method: 'POST',
    body: JSON.stringify({ code }),
  });
  if (!res.ok) {
    const err = await res.json();
    throw new Error(err.error ?? 'Forkert kode');
  }
}

export async function disableTotp(password: string, code: string): Promise<void> {
  const res = await apiFetch('/api/auth/totp/disable', {
    method: 'POST',
    body: JSON.stringify({ password, code }),
  });
  if (!res.ok) {
    const err = await res.json();
    throw new Error(err.error ?? 'Kunne ikke deaktivere 2FA');
  }
}

export async function regenerateRecoveryCodes(code: string): Promise<{ recovery_codes: string[] }> {
  const res = await apiFetch('/api/auth/totp/regenerate-recovery', {
    method: 'POST',
    body: JSON.stringify({ code }),
  });
  if (!res.ok) {
    const err = await res.json();
    throw new Error(err.error ?? 'Kunne ikke genere koder');
  }
  return res.json();
}

// Login med 2FA-trin — accepterer TOTP-, email- eller recovery-kode.
// Endpoint hedder stadig /login-totp af backward-compat årsager.
export async function loginWithTotp(preAuthToken: string, code: string): Promise<{ token: string; user: AuthUser; method_used: TwoFactorMethod | 'recovery'; used_recovery_code: boolean }> {
  const res = await apiFetch('/api/auth/login-totp', {
    method: 'POST',
    body: JSON.stringify({ pre_auth_token: preAuthToken, code }),
  });
  if (!res.ok) {
    const err = await res.json();
    throw new Error(err.error ?? 'Login fejlede');
  }
  return res.json();
}

// Trigger email-kode under login (efter password er verificeret, før kode-input)
export async function sendLoginEmailCode(preAuthToken: string): Promise<void> {
  const res = await apiFetch('/api/auth/login/send-email-code', {
    method: 'POST',
    body: JSON.stringify({ pre_auth_token: preAuthToken }),
  });
  if (!res.ok) {
    const err = await res.json();
    throw new Error(err.error ?? 'Kunne ikke sende kode');
  }
}

// Email-2FA enable/disable (for logged-in bruger)
export async function enableEmail2fa(password: string): Promise<void> {
  const res = await apiFetch('/api/auth/email-2fa/enable', {
    method: 'POST',
    body: JSON.stringify({ password }),
  });
  if (!res.ok) {
    const err = await res.json();
    throw new Error(err.error ?? 'Kunne ikke aktivere email-2FA');
  }
}

export async function disableEmail2fa(password: string, code?: string): Promise<void> {
  const res = await apiFetch('/api/auth/email-2fa/disable', {
    method: 'POST',
    body: JSON.stringify({ password, code }),
  });
  if (!res.ok) {
    const err = await res.json();
    throw new Error(err.error ?? 'Kunne ikke deaktivere email-2FA');
  }
}

// Send email-kode til logged-in bruger (til sensitive aktioner som backup-download)
export async function sendActionEmailCode(): Promise<void> {
  const res = await apiFetch('/api/auth/email-2fa/send-code', { method: 'POST' });
  if (!res.ok) {
    const err = await res.json();
    throw new Error(err.error ?? 'Kunne ikke sende kode');
  }
}

// ─── Profil-opdatering ──────────────────────────────────────────────────────

export async function updateProfile(name: string): Promise<void> {
  const res = await apiFetch('/api/auth/profile', {
    method: 'PUT',
    body: JSON.stringify({ name }),
  });
  if (!res.ok) {
    const err = await res.json();
    throw new Error(err.error ?? 'Kunne ikke gemme');
  }
}

export async function changePassword(currentPassword: string, newPassword: string): Promise<void> {
  const res = await apiFetch('/api/auth/password/change', {
    method: 'POST',
    body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
  });
  if (!res.ok) {
    const err = await res.json();
    throw new Error(err.error ?? 'Kunne ikke ændre password');
  }
}

export async function requestEmailChange(newEmail: string, password: string): Promise<{ pending_email: string }> {
  const res = await apiFetch('/api/auth/email/request-change', {
    method: 'POST',
    body: JSON.stringify({ new_email: newEmail, password }),
  });
  if (!res.ok) {
    const err = await res.json();
    throw new Error(err.error ?? 'Kunne ikke anmode om email-skift');
  }
  return res.json();
}

export async function confirmEmailChange(token: string): Promise<{ new_email: string }> {
  const res = await fetch('/api/auth/email/confirm-change', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token }),
  });
  if (!res.ok) {
    const err = await res.json();
    throw new Error(err.error ?? 'Kunne ikke bekræfte email-skift');
  }
  return res.json();
}

export async function cancelEmailChange(): Promise<void> {
  const res = await apiFetch('/api/auth/email/cancel-change', { method: 'POST' });
  if (!res.ok) {
    const err = await res.json();
    throw new Error(err.error ?? 'Kunne ikke annullere');
  }
}

// ─── Payment-postings (GoMore-konto m.fl.) ────────────────────────────

export type PaymentCategory =
  | 'rental' | 'extra' | 'credit' | 'cancellation' | 'refusion'
  | 'subscription' | 'boost' | 'bank' | 'owed' | 'other';
export type MatchType = 'auto' | 'manual' | 'ignored' | 'unmatched';
export type MatchConfidence = 'exact' | 'high' | 'medium' | 'low' | null;
// pending = det udestående er præcis bookingens endnu ikke betalte poster (ekstra-betalinger,
// egne fakturalinjer) — venter på afregning, er ikke en afvigelse.
export type BalanceStatus = 'ok' | 'missing' | 'overflow' | 'pending';

export interface PaymentPosting {
  id: number;
  posting_date: string;          // YYYY-MM-DD
  description: string;
  renter_name: string | null;
  registration_number: string | null;
  amount: number;
  category: PaymentCategory;
  booking_id: number | null;
  booking_uid: string | null;
  match_type: MatchType;
  match_confidence: MatchConfidence;
  imported_at: string;
  matched_at: string | null;
  notes: string | null;
  saldo: number;                 // beregnet løbende saldo
}

export async function getPaymentPostings(params: {
  from?: string; to?: string; category?: string;
} = {}): Promise<PaymentPosting[]> {
  const q = new URLSearchParams();
  if (params.from) q.set('from', params.from);
  if (params.to)   q.set('to', params.to);
  if (params.category) q.set('category', params.category);
  const res = await apiFetch(`/api/payments/postings?${q.toString()}`);
  if (!res.ok) throw new Error('Kunne ikke hente posteringer');
  return res.json();
}

export interface PaymentSummary {
  by_category: { category: PaymentCategory; count: number; total: number }[];
  total_amount: number;
  total_count: number;
}

export async function getPaymentSummary(params: { from?: string; to?: string } = {}): Promise<PaymentSummary> {
  const q = new URLSearchParams();
  if (params.from) q.set('from', params.from);
  if (params.to)   q.set('to', params.to);
  const res = await apiFetch(`/api/payments/summary?${q.toString()}`);
  if (!res.ok) throw new Error('Kunne ikke hente oversigt');
  return res.json();
}

export interface NormalizedPosting {
  posting_date: string;
  description: string;
  renter_name: string | null;
  registration_number: string | null;
  amount: number;
  category: PaymentCategory;
  raw_json?: string;
}

export interface PreviewAllocation {
  posting_index: number;
  posting: NormalizedPosting;
  booking_id: number | null;
  match_confidence: MatchConfidence;
}

export interface PreviewUnmatched extends NormalizedPosting {
  posting_index: number;
}

export interface SkippedPosting {
  dato: string | null;
  beskrivelse: string | null;
  beloeb: number | null;
  nummerplade: string | null;
  /** pending = afventer udbetaling hos GoMore. no_date/no_amount = uventet, bør undersøges. */
  reason: 'pending' | 'no_date' | 'no_amount';
}

export interface PreviewResult {
  new_count: number;
  duplicate_count: number;
  skipped_count: number;
  skipped: SkippedPosting[];
  allocations: PreviewAllocation[];
  unmatched: PreviewUnmatched[];
  summary: {
    total_postings: number;
    matched: number;
    unmatched: number;
    matched_bookings: number;
    bookings_balanced: number;
    bookings_missing: number;
    bookings_overflow: number;
    total_payout: number;
    total_received: number;
    net_balance: number;
  };
  postings_payload: NormalizedPosting[];
}

export async function previewPaymentImport(files: File[]): Promise<PreviewResult> {
  const form = new FormData();
  for (const f of files) form.append('files', f);
  const res = await apiFetch('/api/payments/preview', { method: 'POST', body: form });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Upload-fejl' }));
    throw new Error(err.error || 'Upload-fejl');
  }
  return res.json();
}

export interface ImportRequest {
  postings: NormalizedPosting[];
  allocations: {
    posting_index: number;
    booking_id: number | null;
    match_type?: 'auto' | 'manual' | 'ignored';
    match_confidence?: 'exact' | 'high' | 'medium' | 'low' | null;
  }[];
}

export async function commitPaymentImport(payload: ImportRequest): Promise<{ inserted: number; skipped: number }> {
  const res = await apiFetch('/api/payments/import', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Import-fejl' }));
    throw new Error(err.error || 'Import-fejl');
  }
  return res.json();
}

export async function setPostingMatch(postingId: number, bookingId: number | null, notes?: string): Promise<void> {
  const res = await apiFetch(`/api/payments/${postingId}/match`, {
    method: 'POST',
    body: JSON.stringify({ booking_id: bookingId, notes }),
  });
  if (!res.ok) throw new Error('Kunne ikke gemme match');
}

export async function ignorePosting(postingId: number): Promise<void> {
  const res = await apiFetch(`/api/payments/${postingId}/ignore`, { method: 'POST' });
  if (!res.ok) throw new Error('Kunne ikke markere som ignoreret');
}

export interface BookingBalance {
  payout: number;
  received: number;
  balance: number;
  status: BalanceStatus;
  days_since_end: number | null;
  postings?: {
    id: number;
    posting_date: string;
    description: string;
    amount: number;
    category: PaymentCategory;
    match_type: MatchType;
    source: string;          // source_key fx 'gomore', 'bank'
    account_name: string;    // visning fx 'GoMore', 'Bank'
  }[];
}

export async function getBookingBalance(bookingId: number): Promise<BookingBalance> {
  const res = await apiFetch(`/api/payments/booking/${bookingId}/balance`);
  if (!res.ok) throw new Error('Kunne ikke hente balance');
  return res.json();
}

export async function getAllBookingBalances(): Promise<Record<number, BookingBalance>> {
  const res = await apiFetch('/api/payments/booking-balances');
  if (!res.ok) throw new Error('Kunne ikke hente balancer');
  return res.json();
}

// ─── Konti til afstemning ───────────────────────────────────────────

export interface PaymentAccount {
  id: number;
  name: string;
  source_key: string;
  is_built_in: number;       // tinyint
  is_active: number;
  sort_order: number;
  notes: string | null;
}

export async function getPaymentAccounts(): Promise<PaymentAccount[]> {
  const res = await apiFetch('/api/payments/accounts');
  if (!res.ok) throw new Error('Kunne ikke hente konti');
  return res.json();
}

export async function createPaymentAccount(data: { name: string; source_key: string; is_active?: boolean; sort_order?: number; notes?: string | null }): Promise<{ id: number }> {
  const res = await apiFetch('/api/payments/accounts', { method: 'POST', body: JSON.stringify(data) });
  if (!res.ok) { const e = await res.json(); throw new Error(e.error ?? 'Kunne ikke oprette konto'); }
  return res.json();
}

export async function updatePaymentAccount(id: number, data: { name: string; source_key: string; is_active?: boolean; sort_order?: number; notes?: string | null }): Promise<void> {
  const res = await apiFetch(`/api/payments/accounts/${id}`, { method: 'PUT', body: JSON.stringify(data) });
  if (!res.ok) { const e = await res.json(); throw new Error(e.error ?? 'Kunne ikke gemme konto'); }
}

export async function deletePaymentAccount(id: number): Promise<void> {
  const res = await apiFetch(`/api/payments/accounts/${id}`, { method: 'DELETE' });
  if (!res.ok) { const e = await res.json(); throw new Error(e.error ?? 'Kunne ikke slette konto'); }
}

export async function createManualPosting(data: {
  booking_id?: number | null;
  source_key: string;
  posting_date: string;
  amount: number;
  fee?: number;
  description?: string;
  category?: PaymentCategory;
  notes?: string;
}): Promise<{ id: number; fee_applied?: number }> {
  const res = await apiFetch('/api/payments/postings', { method: 'POST', body: JSON.stringify(data) });
  if (!res.ok) { const e = await res.json(); throw new Error(e.error ?? 'Kunne ikke gemme betaling'); }
  return res.json();
}

export async function deleteManualPosting(id: number): Promise<void> {
  const res = await apiFetch(`/api/payments/postings/${id}`, { method: 'DELETE' });
  if (!res.ok) { const e = await res.json(); throw new Error(e.error ?? 'Kunne ikke slette postering'); }
}

export interface BookingSearchHit {
  id: number;
  uid: string;
  registration_number: string;
  renter_name: string;
  s: string;
  e: string;
  invoice_number: string | null;
  payout: number;
}

export async function searchBookingsForMatch(q: string): Promise<BookingSearchHit[]> {
  const res = await apiFetch(`/api/payments/search-bookings?q=${encodeURIComponent(q)}`);
  if (!res.ok) throw new Error('Kunne ikke søge bookings');
  return res.json();
}

/** Booking der stadig mangler penge. `suggested` = udestående passer med det beløb der matches. */
export interface OpenItem extends Omit<BookingSearchHit, 'payout'> {
  expected: number;
  received: number;
  outstanding: number;
  suggested: boolean;
}

// ─── Aflyste udlejninger ────────────────────────────────────────────

export interface Cancellation {
  id: number;
  gomore_rental_id: string;
  invoice_number: string | null;
  invoice_date: string | null;
  renter_name: string | null;
  vehicle_model: string | null;
  registration_number: string | null;
  starts_at: string | null;
  ends_at: string | null;
  /** Med fortegn: positivt = lejeren aflyste, negativt = du aflyste selv */
  fee: number;
  /** Som anført på kvitteringen. null = bilaget oplyser ingen moms (kunde uden for EU) */
  vat_amount: number | null;
  vat_type: 'salgsmoms' | 'koebsmoms' | null;
  direction: 'income' | 'expense';
  /** Hvad udlejningen ville have indbragt */
  rental_value: number | null;
  posting_id: number | null;
  posting_date: string | null;
}

export async function getCancellations(): Promise<Cancellation[]> {
  const res = await apiFetch('/api/cancellations');
  if (!res.ok) throw new Error('Kunne ikke hente aflysninger');
  return res.json();
}

export interface CancellationImportResult {
  created: number;
  updated: number;
  skipped: number;
  total: number;
  errors: { rental_id: string; error: string }[];
}

export async function importCancellations(rentals: unknown[]): Promise<CancellationImportResult> {
  const res = await apiFetch('/api/cancellations/import', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ rentals }),
  });
  if (!res.ok) throw new Error('Import fejlede');
  return res.json();
}

/** Indtægt der ikke hører til en udlejning — i dag aflysningsgebyrer. */
export interface OtherIncome {
  id: number;
  posting_date: string;
  description: string;
  renter_name: string | null;
  registration_number: string | null;
  amount: number;
  category: string;
  /** Fra kvitteringen, ikke beregnet. null = aflysningen er ikke importeret, eller bilaget
   *  oplyser ingen moms (kunde uden for EU). */
  vat_amount: number | null;
  vat_type: 'salgsmoms' | 'koebsmoms' | null;
  invoice_number: string | null;
}

export async function getOtherIncome(): Promise<OtherIncome[]> {
  const res = await apiFetch('/api/payments/other-income');
  if (!res.ok) throw new Error('Kunne ikke hente øvrige indtægter');
  return res.json();
}

export async function getOpenItems(amount?: number): Promise<OpenItem[]> {
  const qs = amount != null ? `?amount=${encodeURIComponent(String(amount))}` : '';
  const res = await apiFetch(`/api/payments/open-items${qs}`);
  if (!res.ok) throw new Error('Kunne ikke hente åbne poster');
  return res.json();
}

// ─── Stripe Connect (Bolge 3.4) ─────────────────────────────────────

export interface StripeConnectStatus {
  account_id: string | null;
  status: 'not_connected' | 'pending' | 'active' | 'disabled';
  charges_enabled?: boolean;
  payouts_enabled?: boolean;
  application_fee_percent: number;
  configured: boolean;  // false hvis platform endnu ikke har sat STRIPE_SECRET_KEY
}

export async function getStripeConnectStatus(): Promise<StripeConnectStatus> {
  const res = await apiFetch('/api/stripe-connect/status');
  if (!res.ok) throw new Error('Kunne ikke hente Stripe-status');
  return res.json();
}

export async function startStripeConnect(): Promise<{ url: string }> {
  const res = await apiFetch('/api/stripe-connect/onboard', { method: 'POST' });
  if (!res.ok) { const e = await res.json(); throw new Error(e.error ?? 'Stripe Connect ikke konfigureret'); }
  return res.json();
}

export async function disconnectStripe(): Promise<void> {
  const res = await apiFetch('/api/stripe-connect', { method: 'DELETE' });
  if (!res.ok) throw new Error('Kunne ikke afbryde forbindelsen');
}

export async function setStripeApplicationFee(percent: number): Promise<void> {
  const res = await apiFetch('/api/stripe-connect/fee', {
    method: 'PUT',
    body: JSON.stringify({ percent }),
  });
  if (!res.ok) { const e = await res.json(); throw new Error(e.error ?? 'Kunne ikke gemme'); }
}

export interface StripeBalance {
  available: { amount: number; currency: string };
  pending: { amount: number; currency: string };
}

export async function getStripeBalance(): Promise<StripeBalance> {
  const res = await apiFetch('/api/stripe-connect/balance');
  if (!res.ok) { const e = await res.json(); throw new Error(e.error ?? 'Kunne ikke hente saldo'); }
  return res.json();
}

export async function createStripePayout(amount?: number): Promise<{ payout_id: string; amount: number; currency: string }> {
  const res = await apiFetch('/api/stripe-connect/payout', {
    method: 'POST',
    body: JSON.stringify(amount != null ? { amount } : {}),
  });
  if (!res.ok) { const e = await res.json(); throw new Error(e.error ?? 'Udbetaling fejlede'); }
  return res.json();
}

export async function getStripeDashboardLink(): Promise<{ url: string }> {
  const res = await apiFetch('/api/stripe-connect/dashboard-link');
  if (!res.ok) { const e = await res.json(); throw new Error(e.error ?? 'Kunne ikke generere link'); }
  return res.json();
}

